import React, {useEffect, useState, useMemo, useCallback, useContext} from "react";
import {useTranslation} from "react-i18next";
import Aux from "../../../../hoc/Auxiliary";
import TableContainer from "./Table/TableContainer";
import DateFormated from "../../../Utils/Date/Date";
import formatDate from "../../../Utils/Date/formatDate";
import DateFilters, {DEFAULT_DATE, TODAY} from "../Tables/DateFilters";
import Spinner from "../../../UI/Spinner";
import Export from "../../../Utils/XLS/Export";
import DisplaySwitcher from "../../../UI/Toolbar/DisplaySwitcher";
import CardGeneric from "../../../Cards/CardGeneric";
import Sorter from "../../../UI/Toolbar/Sorter";
import axios from "../../../../axios-defaults";
import SelectWrapper from "../../../Utils/Input/SelectWrapper";
import Pagination from "../../../UI/Pagination";
import Currency from "../../../Utils/Number/Currency";
import GMaps from "../../../GMaps/GMaps";
import {fireGTMDownloadFileEvent} from "../../../Utils/GTM/GTMHelper";
import {userHaveSpecificPermission} from "../../../Utils/UserHelper";
import UserContext from "../../../../context/user-context";

const transactions = (props) => {
    const {startDate, endDate, pageNumber, pageSize, currentPeriod, subAccountCode, customFilters, isOnlinePaymentAllowed} = props;
    const {t, i18n} = useTranslation();
    const [itemsRaw, setItemsRaw] = useState([]);
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [items, setItems] = useState([]);
    const [transactionsTotal, setTransactionsTotal] = useState(0.0);
    const [filters, setFilters] = useState({owner: ''});
    const [owners, setOwners] = useState([]);
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('DESC');
    const [sortTableField, setSortTableField] = useState('date');
    const [sortTableDirection, setSortTableDirection] = useState('DESC');
    const [checked, setChecked] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({currentPage: 1, itemsPerPage: 6, totalPages: null});
	const { user } = useContext(UserContext);

    const getTransactions = async () => {
        let params = '';
        if (pageNumber && pageSize) {
            params = `subAccountCode=${subAccountCode}&pageNumber=${pageNumber}&pageSize=${pageSize}`
        } else {
            if (startDate === null || endDate === null) {
                return;
            } else {
                const currentStartDate = (startDate)?startDate:DEFAULT_DATE;
                const currentEndDate = (endDate)?endDate:formatDate(TODAY);
                params = `subAccountCode=${subAccountCode}&currentPeriod=${currentPeriod}&startDate=${currentStartDate}&endDate=${currentEndDate}`;
            }
        }


        await axios.get(`/subAccounts/transactionLines?${params}`)
            .then(response => {
                if(response.data.succeeded) {
                    setItemsRaw(response.data.data);
                    mapTransactions(response.data.data);
                }
            }).catch(error => {
                console.log('[Account.js] : Transactions ajax error in getTransactions()', error.message)
            });
    }

    const mapTransactions = (transactions) => {
        let ownersArr = [];
        let itemsArr = [];

        transactions.forEach((item) => {
            let itemID = item.supplyPointLongitude + "" + item.supplyPointLatitude;
            let newItem = {
                checkBox: item.invoiceNumber,
                invoiceNumber: item.invoiceNumber,
                card: {
                    cardHolder: item.cardOwner,
                    cardNumber: item.cardNumber,
                },
                supplyPoint: {
                    id: itemID,
                    name: item.supplyPointName,
                    address: item.supplyPointAddress,
                    banner: item.supplyPointBanner,
                    latitude: item.supplyPointLatitude,
                    longitude: item.supplyPointLongitude
                },
                quantity: item.quantity,
                price: item.netPrice,
                amount: item.netAmount,
                date: item.transactionDateTime,
                productName: item.productName,
                amountBalance: item.balanceAmount
            };
            ownersArr.push(item.cardOwner + ' (' + item.cardNumber.slice(item.cardNumber.length - 4) +')');
            itemsArr.push(newItem);

        });
        setItems(itemsArr);
        setOwners([...new Set(ownersArr)]);
        if (customFilters) {
            setFilters(customFilters);
        }
    }

    useEffect(async () => {
        setLoading(true);
        await getTransactions();
        setLoading(false);
    }, [startDate, endDate, pageNumber, pageSize])

    const filteredItems = useMemo(() => {
        let newItems = [...items];
        let filteredData = newItems.filter(item => {
            let isMatching = true;
            Object.entries(filters).forEach(entry  => {
                const [key, value] = entry;
                if (value) {
                    switch(key){
                        case 'owner':
                            var regex = /(.+) \((\d+)\)/;
                            const result = value.match(regex);
                            const matchOwner = result[1];
                            const matchCard = result[2];
                            isMatching = item.card.cardHolder === matchOwner && isMatching;
                            isMatching = item.card.cardNumber.slice(item.card.cardNumber.length - 4) === matchCard && isMatching;
                            break;
                        default: isMatching = true;
                    }
                }
            })
            return isMatching;
        });

        return filteredData;
    }, [items, filters]);

    useEffect(() => {
        let totalAmount = 0.0;
        filteredItems.forEach((item) => {
            totalAmount += item.amount?.value;
        });
        setTransactionsTotal(totalAmount);
    }, [filteredItems]);

    useEffect(
        () =>
            isSelectAll
                ? setChecked(itemsRaw.map((item) => item.invoiceNumber.toString()))
                : setChecked([]),
        [isSelectAll]
    );

    const handleCheck = useCallback(
        (isChecked, value) => {
            if (isChecked && !checked.includes(value.toString())) {
                setChecked((checked) => [
                    ...checked.filter((thisChecked) => thisChecked !== value),
                    value,
                ]);
            } else {
                setChecked((checked) =>
                    checked.filter((thisChecked) => thisChecked !== value)
                );
            }
        },
        [checked]
    );

    const doXLSDetailExport = useCallback((fileType) => {

        const checkedTransactions = itemsRaw
            .filter((item) => checked.includes(item.invoiceNumber.toString()))
            .map((transaction) => {
				let cols = {}

				cols[t("export.transactionLines.subAccountCode")]=transaction.subAccountCode;
				cols[t("export.transactionLines.invoiceNumber")]=transaction.invoiceNumber;
				cols[t("export.transactionLines.transactionDateTime")]=new Date(transaction.transactionDateTime + 'Z');
				cols[t("export.transactionLines.cardNumber")]=transaction.cardNumber;
				cols[t("export.transactionLines.cardOwner")]=transaction.cardOwner;
				cols[t("export.transactionLines.productCode")]=transaction.productCode;
				cols[t("export.transactionLines.productName")]=
					transaction.productName.values[
						transaction.productName.keys.indexOf(i18n.language)
						];
				cols[t("export.transactionLines.quantity")]=
					(transaction.quantity.value !== null?Number(transaction.quantity.value.toFixed(2)):'');

				if (userHaveSpecificPermission(user,'financial_data')) {
					cols[t("export.transactionLines.grossPrice")] = transaction.grossPrice?.value;
					cols[t("export.transactionLines.grossAmount")] = transaction.grossAmount?.value;
					cols[t("export.transactionLines.provincialExciseTaxAmount")] = transaction.provincialExciseTaxAmount?.value;
					cols[t("export.transactionLines.federalExciseTaxAmount")] = transaction.federalExciseTaxAmount?.value;
					cols[t("export.transactionLines.gstAmount")] = transaction.gstAmount?.value;
					cols[t("export.transactionLines.pstAmount")] = transaction.pstAmount?.value;
					cols[t("export.transactionLines.netAmount")] = transaction.netAmount?.value;
				}

				cols[t("export.transactionLines.address")]=
					transaction.supplyPointAddress.addressLine1 +
					((transaction.supplyPointAddress.addressLine2) ? ", " + transaction.supplyPointAddress.addressLine2 : "");
				cols[t("export.transactionLines.city")]=transaction.supplyPointAddress.city,
				cols[t("export.transactionLines.stateOrProvinceCode")]=transaction.supplyPointAddress.stateOrProvinceCode,
				cols[t("export.transactionLines.isNonHighwayFuelQuantity")]=((i18n.language === "fr") ? transaction.isNonHighwayFuel.descriptionFr : transaction.isNonHighwayFuel.descriptionEn),
				cols[t("export.transactionLines.date")]=transaction.transactionDateTime.split("T")[0],
				cols[t("export.transactionLines.time")]=transaction.transactionDateTime.split("T")[1],
				cols[t("export.transactionLines.odometer")]=transaction.odometer.value,
				cols[t("export.transactionLines.miscellaneous")]=transaction.miscellaneous,
				cols[t("export.transactionLines.carrierDeliveryNumber")]=transaction.carrierDeliveryNumber,
				cols[t("export.transactionLines.statementDate")]=transaction.statementDate?.split("T")[0];
				cols[t("export.transactionLines.dueDate")]=transaction.dueDate.split("T")[0]

				return cols;
				}
			);
        Export(checkedTransactions, "harnois_export", [
            t("export.transactionLines.netAmount")
        ], fileType);
		fireGTMDownloadFileEvent('transaction', `${fileType} - Detaille`);
    }, [checked, props.items]);

    const doXLSExport = useCallback(async (fileType) => {
        setLoading(true);
        const mappedTransactions =
            itemsRaw
                .filter((transaction) =>
                    checked.includes(transaction.invoiceNumber.toString())
                )
                .map((item) => {
					let cols = {}

					cols[t("export.transactions.subAccountCode")]=item.subAccountCode;
					cols[t("export.transactions.transactionDateTime")]=new Date(item.transactionDateTime + 'Z');
					cols[t("export.transactions.invoiceNumber")]=item.invoiceNumber;
					cols[t("export.transactions.dueDate")]=new Date(item.dueDate + 'Z');

					if (userHaveSpecificPermission(user,'financial_data')) {
						cols[t("export.transactions.netAmount")] = item.netAmount?.value;
						cols[t("export.transactions.balanceAmount")] = item.balanceAmount?.value;
					}

                    return cols
                });

        Export(mappedTransactions, "harnois_export", [t("export.transactions.netAmount"), t("export.transactions.balanceAmount")], fileType);
        setLoading(false);
		fireGTMDownloadFileEvent('transaction', `${fileType} - Sommaire`);
    }, [startDate, endDate, checked]);

    const doPDFExport = useCallback(() => {
    }, []);

    let sorter = null;
    const setSorterRef = element => sorter = element;

    const storeSortedItems = (sortedItems) => {
        if (sortedItems && sortedItems.length) {
            setItems(sortedItems);
        }
    }

    const handleSort = useCallback(sortBy => {
        if (sortBy.length > 0) {
            setSortField(sortBy[0].id);
            setSortDirection(sortBy[0].desc?'DESC':'ASC')
        }
    }, [])

    const setSortByCallback = useMemo(() =>
            [{desc:sortTableDirection==='DESC', id:sortTableField}]
        , [sortTableDirection, sortTableField])

    const setSorterCallback = (columnId, direction) => {
        setSortTableField(columnId)
        setSortTableDirection(direction)
    }

    const onPageChanged = data => {
        const {currentPage, totalPages} = data;
        setPagination({
            ...pagination,
            currentPage: currentPage,
            totalPages: totalPages,
        });
    }

    const objectCardNumberSort = useMemo(
        () => (rowA, rowB, columnId) => {
            const valueA = rowA.values[columnId].cardNumber;
            const valueB = rowB.values[columnId].cardNumber;
            const a = valueA.slice(valueA.length - 4);
            const b = valueB.slice(valueB.length - 4);
            return a > b ? 1 : -1;
        },
        []
    );
    const objectFloatSort = useMemo(
        () => (rowA, rowB, columnId) => {
            const a = parseFloat(rowA.values[columnId].value.toFixed(2));
            const b = parseFloat(rowB.values[columnId].value.toFixed(2));
            return a > b ? 1 : -1;
        },
        []
    );

	let cols = [
		{
			Header: (
				<div className="form-check flex flex-row justify-content-end align-items-end">
					<input
						type="checkbox"
						id="select_all"
						className="form-check-input"
						value={isSelectAll}
						onChange={(v) => setIsSelectAll(v.target.checked)}
					/>
				</div>
			),
			accessor: "checkBox",
			Cell: (props) => {
				if (props.value) {
					return (
						<div className="form-check flex flex-row justify-content-end">
							<input
								type="checkbox"
								value={props.value}
								className="form-check-input"
								onChange={(v) => {
									handleCheck(
										v.target.checked,
										v.target.value
									);
								}}
								checked={
									!!checked.includes(props.value.toString())
								}
							/>
						</div>
					);
				} else {
					return '';
				}
			},
			disableSortBy: true,
		},
		{
			Header: t("account.tabs.transactions.table.transaction_number_header"),
			accessor: "invoiceNumber",
			Cell: (props) => {
				if (props.value) return props.value;
				else return "";
			},
		},
		{
			Header: t("account.tabs.transactions.table.card_header"),
			accessor: "card",
			sortType: objectCardNumberSort,
			Cell: (props) => {
				if (props.value) {
					return (
						<Aux>
							<strong>{props.value.cardHolder}</strong>
							<br/>
							<small>
								XXXX&nbsp;XXXX&nbsp;XXXX&nbsp;
								{props.value.cardNumber.slice(props.value.cardNumber.length - 4)}
							</small>
						</Aux>
					);
				} else {
					return '';
				}
			},
		},
		{
			Header: t("account.tabs.transactions.table.supply_point_header"),
			accessor: "supplyPoint",
			Cell: (props) => {
				if (props.value) {
					let address = "";
					return (
						<Aux>
							<strong>{props.value.name}</strong>
							<br/>
							<small>
								{Object.values(props.value.address)
									.filter((el) => {
										return el != null && el !== "";
									})
									.join(", ")}
							</small>
						</Aux>
					);
				} else {
					return '';
				}
			},
		},
		{
			Header: t("account.tabs.transactions.table.product_name"),
			accessor: "productName",
			Cell: (props) => {
				if (props.value.values && props.value.values.length > 0) {
					let val = ((i18n.language === "fr") ? props.value.values[1] : props.value.values[0])
					return <span className="product-name">{val}</span>;
				} else {
					return "";
				}
			},
		},
		{
			Header: t("account.tabs.transactions.table.quantity_header"),
			accessor: "quantity",
			sortType: objectFloatSort,
			Cell: (props) => {
				if (props.value && props.value.value) {
					return (
						<span className="amount-qty">
                                {props.value.value.toFixed(2) + " " + props.value.unit}
                            </span>
					);
				} else {
					return "";
				}
			},
		}];

	if(userHaveSpecificPermission(user,'financial_data')) {
		cols.push({
			Header: t("account.tabs.transactions.table.price_header"),
			accessor: "price",
			sortType: objectFloatSort,
			Cell: (props) => {
				if (props.value && props.value.value) {
					return (
						<span className="amount-qty">
								{props.value.value.toFixed(2) + props.value.unit}
							</span>
					);
				} else {
					return "";
				}
			},
		},
		{
			Header: t("account.tabs.transactions.table.amount_header"),
			accessor: "amount",
			sortType: objectFloatSort,
			Cell: (props) => {
				if (props.value && props.value.value) {
					return (
						<span className="amount-price">
							<Currency value={props.value.value}/>
						</span>
					);
				} else {
					return '';
				}
			},
		});
	}

	cols.push({
			Header: t("account.tabs.transactions.table.date_header"),
			accessor: "date",
			sort: "desc",
			Cell: (props) => {
				if (props.value) {
					return (
						<DateFormated date={props.value} withTime={true}/>
					);
				} else {
					return '';
				}
			},
			sortMethod: (a, b) => {
				var a1 = new Date(a).getTime();
				var b1 = new Date(b).getTime();
				if (a1 < b1) return 1;
				else if (a1 > b1) return -1;
				else return 0;
			},
		});


	if(userHaveSpecificPermission(user,'financial_data')) {
		cols.push({
			Header: t("account.tabs.transactions.table.balance_amount"),
			accessor: "amountBalance",
			sortType: objectFloatSort,
			Cell: (props) => {
				if (props.value && props.value.value) {
					return (
						<span className="balance-price">
                                <Currency value={props.value.value}/>
                            </span>
					);
				} else {
					return '';
				}
			},
		});
	}

    const columns = useMemo(
        () => cols,
        [checked, items, t, i18n]
    );

    const resetFilterHandler = () => {
        setFilters({owner:''});
    }

    const filterHandler = (filter, value) => {
        const newFilters = { ...filters, [filter] : value }
        setFilters(newFilters)
    };

	let sortOptions = [
		{
			label: t("account.tabs.transactions.table.date_header"),
			id: 'date',
			value: 'date',
			type: 'date'
		}, {
			label: t("account.tabs.transactions.table.transaction_number_header"),
			id: 'invoiceNumber',
			value: 'invoiceNumber',
			type: 'int'
		}, {
			label: t("account.tabs.transactions.table.card_number_header"),
			id: 'cardNumber',
			value: 'card.cardNumber',
			type: 'card'
		}, {
			label: t("account.tabs.transactions.table.card_header"),
			id: 'card',
			value: 'card.cardHolder',
			type: 'string'
		}, {
			label: t("account.tabs.transactions.table.supply_point_header"),
			id: 'supplyPoint',
			value: 'supplyPoint.name',
			type: 'string'
		}, {
			label: t("account.tabs.transactions.table.product_name"),
			id: 'productName',
			value: 'productName',
			type: 'string'
		}, {
			label: t("account.tabs.transactions.table.quantity_header"),
			id: 'quantity',
			value: 'quantity.value',
			type: 'float'
		}];

	if (userHaveSpecificPermission(user,'financial_data')) {
		sortOptions.push({
			label: t("account.tabs.transactions.table.price_header"),
			id: 'price',
			value: 'price.value',
			type: 'float'
		}, {
			label: t("account.tabs.transactions.table.amount_header"),
			id: 'amount',
			value: 'amount.value',
			type: 'float'
		})
	}

    let dataHtml = '';
    if (loading) {
        dataHtml = <Spinner/>;
    } else {
        let transactionSummaryHtml = "";

        if(filteredItems.length !== 0){
            transactionSummaryHtml = (
                <div className="section section--padding--60">
                    <div className="row">
                        <div className="tab__message col-xs-12 col-sm-6">
                            <h4 className="headline headline--sub headline--c-red">
                                {itemsRaw.length > 1
                                    ? filteredItems.length + " transactions"
                                    : filteredItems.length + " transaction"}
                            </h4>
                            <div>
                                {t("account.tabs.transactions.drivers_transactions")}
                            </div>
                        </div>
						{
							transactionsTotal ?
								<div className="tab__amount col-xs-12 col-sm-6 ">
									<p className="p--margin--0">
										{t("account.tabs.transactions.table.amount_header")}
									</p>
									<div className="price">
										<span className="amount-price">
											<Currency value={transactionsTotal.toFixed(2)}  />
										</span>
									</div>
								</div> : null
						}

                    </div>
                </div>
            );
        }else{
            if(startDate !== null && endDate !== null){
                transactionSummaryHtml = (<strong>{t("account.tabs.transactions.nodata")}</strong>);
            }
        }

        if (props.currentDisplayMode === 'list') {
			let paymentAllowed = isOnlinePaymentAllowed;
			if (!userHaveSpecificPermission(user,'financial_data')) {
				paymentAllowed = false;
			}
            dataHtml = (
                <Aux>
                    {transactionSummaryHtml}
                    <TableContainer
                        columns={columns}
                        data={filteredItems}
                        doXLSExport={doXLSExport}
                        doXLSDetailExport={doXLSDetailExport}
                        checked={checked}
                        onSort={handleSort}
                        setSortByCallback={setSortByCallback}
                        initialStateField={sortField}
                        initialStateDirection={sortDirection}
                        isOnlinePaymentAllowed={paymentAllowed}
                    />
                </Aux>
            );
        } else if (props.currentDisplayMode === 'card') {
            const pageLimit = pagination.itemsPerPage;
            const currentPage = pagination.currentPage;
            const offset = (currentPage - 1) * pageLimit;
            const itemsPaged = filteredItems.slice(offset, offset + pageLimit);
            const totalCurrentItems = (filteredItems) ? filteredItems.length : 0;

            dataHtml = (
                <Aux>
                    {transactionSummaryHtml}
                    <div className="row">
                        {itemsPaged.map((item, index) => {
                            const localeAccessor = (i18n.language === "fr") ? 'descriptionFr' : 'descriptionEn';
                            return (
                                <div className="col-12 col-sm-6 col-md-4 mb-4" key={index}>
                                    <CardGeneric title={item.card.cardHolder}>
                                        <>
                                            <p>XXXX&nbsp;XXXX&nbsp;XXXX&nbsp;{item.card.cardNumber.slice(item.card.cardNumber.length - 4)}</p>
                                            <p>{t("account.tabs.transactions.table.transaction_number_header")} : {item.invoiceNumber}</p>
                                            <p><DateFormated date={item.date} withTime={true}/></p>
                                            <p>
                                                <strong>{item.supplyPoint.name}</strong><br/>
                                                <small>
                                                    {Object.values(item.supplyPoint.address)
                                                        .filter((el) => {
                                                            return el != null && el !== "";
                                                        })
                                                        .join(", ")}
                                                </small>
                                            </p>
                                            <div className="row card-row-spliter">
                                                <div className="col-6">
                                                    <p>  {t('account.tabs.transactions.table.quantity_header')}<br/>
                                                    <strong className="price--small" style={{whiteSpace: 'nowrap'}} >{item.quantity.formattedUnitValue}</strong></p>
                                                </div>
                                                <div className="col-6 ">
                                                    <p> {t('account.tabs.transactions.table.product_name')}<br/>
                                                        <strong>{(i18n.language === "fr"
                                                            ? item.productName.values[1]
                                                            : item.productName.values[0])}</strong></p>
                                                </div>

                                            </div>
											{
												item.price ?
													<div className="row">
														<div className="col-6">
															<p> {t('account.tabs.transactions.table.price_header')}<br/>
																<strong>{item.price?.formattedUnitValue}</strong></p>
														</div>
														<div className="col-6">
															<p> {t('account.tabs.transactions.table.amount_header')}<br/>
																<strong><Currency value={item.amount?.value}  /></strong></p>
														</div>
													</div> : null
											}

                                        </>
                                    </CardGeneric>
                                </div>
                            );
                        })}
                    </div>
                    <Pagination totalRecords={totalCurrentItems} pageLimit={pagination.itemsPerPage} pageNeighbours={1} onPageChanged={onPageChanged} />
                </Aux>
            );
        } else if (props.currentDisplayMode === 'map') {

            if (filteredItems.length) {
                let supplyPoints = [];

                Object.keys(filteredItems).map(function (keyName, keyIndex) {
                    let itemID = filteredItems[keyName].supplyPoint.id;

                    if (supplyPoints.hasOwnProperty(itemID)) {
                        supplyPoints[itemID].transactions.push(filteredItems[keyName]);
                    } else {
                        supplyPoints[itemID] = {
                            name: filteredItems[keyName].supplyPoint.name,
                            banner: filteredItems[keyName].supplyPoint.banner,
                            position: {
                                lat: filteredItems[keyName].supplyPoint.latitude,
                                lng: filteredItems[keyName].supplyPoint.longitude,
                            },
                            transactions: [filteredItems[keyName]]
                        };
                    }
                });
                let tableContainerProps = {
                    columns:columns,
                    doXLSExport:doXLSExport,
                    doXLSDetailExport:doXLSDetailExport,
                    checked:checked,
                    onSort:handleSort,
                    setSortByCallback:setSortByCallback,
                    initialStateField:sortField,
                    initialStateDirection:sortDirection
                }

                dataHtml = (
                    <Aux>
                        {transactionSummaryHtml}
                        <GMaps
                            supplyPoints={supplyPoints}
                            dataFilterType={props.dateFilterType}
                            tableContainerProps={tableContainerProps}
                        >
                            <Sorter
                                sortOptions={[
                                    {
                                        label: t("account.tabs.transactions.table.date_header"),
                                        id: 'date',
                                        value: 'date',
                                        type: 'date'
                                    }, {
                                        label: t("account.tabs.transactions.table.transaction_number_header"),
                                        id: 'invoiceNumber',
                                        value: 'invoiceNumber',
                                        type: 'int'
                                    }, {
                                        label: t("account.tabs.transactions.table.card_number_header"),
                                        id: 'cardNumber',
                                        value: 'card.cardNumber',
                                        type: 'card'
                                    }, {
                                        label: t("account.tabs.transactions.table.card_header"),
                                        id: 'card',
                                        value: 'card.cardHolder',
                                        type: 'string'
                                    }, {
                                        label: t("account.tabs.transactions.table.supply_point_header"),
                                        id: 'supplyPoint',
                                        value: 'supplyPoint.name',
                                        type: 'string'
                                    }, {
                                        label: t("account.tabs.transactions.table.product_name"),
                                        id: 'productName',
                                        value: 'productName',
                                        type: 'string'
                                    }, {
                                        label: t("account.tabs.transactions.table.quantity_header"),
                                        id: 'quantity',
                                        value: 'quantity.value',
                                        type: 'float'
                                    }, {
                                        label: t("account.tabs.transactions.table.price_header"),
                                        id: 'price',
                                        value: 'price.value',
                                        type: 'float'
                                    }, {
                                        label: t("account.tabs.transactions.table.amount_header"),
                                        id: 'amount',
                                        value: 'amount.value',
                                        type: 'float'
                                    }
                                ]}
                                items={filteredItems}
                                getSortedItems={storeSortedItems}
                                setSortByCallback={setSorterCallback}
                                sortDirection={sortDirection}
                                sortField={sortField}
                                currentDisplayMode={props.currentDisplayMode}
                            />

                        </GMaps>

                    </Aux>
                )
            } else {
                dataHtml = (
                    <div>
                        <p>
                            {t("account.tabs.transactions.nodata")}
                        </p>
                    </div>
                )
            }


        }
    }

    return (
        <Aux>
            <h2 className="headline headline--section headline--uppercase">
                {t("account.tabs.transactions.title")}
            </h2>
            <div className="flex top-actions top-actions--early">
                <DateFilters
                    startDate={props.startDate}
                    endDate={props.endDate}
                    setStartDate={props.setStartDate}
                    setEndDate={props.setEndDate}
                    changeDateFilterType={props.changeDateFilterType}
                    dateFilterType={props.dateFilterType}
                	displayCurrentPeriod={props.displayCurrentPeriod}
                    enableCurrentPeriod={props.enableCurrentPeriod}
                    component="transactions"
                    labelFilterAll={t("docs.invoices.table.filter.all.transactions")}
                />
                <div className="datatable__filters flex mr-0 mr-xs-2">
                    {(props.currentDisplayMode != 'map') ? (<strong>{t('misc.filter')}&nbsp;&nbsp;</strong>) : (
                        <strong>{t('misc.mapfilter')}&nbsp;&nbsp;</strong>)}
                    <SelectWrapper>
                        <select id="cardholder-filters"
                            onChange={(e) => filterHandler('owner', e.target.value)}
                            value={filters.owner}
                        >
                            <option key="Card holder"
                                    value="">{t("account.tabs.transactions.table.card_header")}</option>
                            {owners.map(name => {
                                return (
                                    <option value={name} key={name}>{name}</option>
                                );
                            })}
                        </select>
                    </SelectWrapper>
                </div>


            </div>
            <div className="flex align-items-center justify-content-end transactions-actions">
                {(props.currentDisplayMode != 'map') ? (
                    <Sorter
                        sortOptions={sortOptions}
                        items={filteredItems}
                        getSortedItems={storeSortedItems}
                        setSortByCallback={setSorterCallback}
                        sortDirection={sortDirection}
                        sortField={sortField}
                        currentDisplayMode={props.currentDisplayMode}
                    />) : ''}
                <DisplaySwitcher currentDisplayMode={props.currentDisplayMode} setDisplayMode={props.setDisplayMode}
                                 mapMode="true"/>
            </div>
            {dataHtml}
        </Aux>
    );
};

export default transactions;
